import type { GridColDef } from '@mui/x-data-grid';
import type { PaymentOverview } from '@weft/api-definition';
import type { ValueOf } from '@weft/utils';
import { isNotNullOrUndefined } from '@weft/utils';
import { dayjs } from '@weft/utils';
import { compareDatesDescending, formatDate } from 'apps/app/src/utils/date';
import { Price } from '../../../components/design/Price';
import { getEmptyCellContent } from '../../../services/table/get-empty-cell-content';
import { PaymentOverviewTableDotMenu } from './payments-page-components/PaymentOverviewTableDotMenu';

export const PAYMENTS_COLUMNS_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  AMOUNT: 'amount',
  CURRENCY: 'currency',
  PAYMENT_PROVIDER: 'paymentMethod',
  STATUS: 'status',
  CREATION_TIME: 'externalPayment.chargeTime',
  UPDATED_AT: 'updatedAt',
  DOT_MENU: 'dotMenu',
});

export type PaymentsColumnsKey = ValueOf<typeof PAYMENTS_COLUMNS_KEYS>;

export const PAYMENT_OVERVIEW_TABLE_COLUMNS: GridColDef<PaymentOverview>[] = [
  {
    field: PAYMENTS_COLUMNS_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'pl-6',
    cellClassName: 'pl-6',
    flex: 1,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    flex: 1,
    renderCell: (params) => {
      const amount = params.row.amount;
      const currency = params.row.currency;

      return (
        <span className="text-label font-sans">
          <Price
            className="font-sans text-black"
            fontSize={14}
            currency={currency}
          >
            {amount}
          </Price>
        </span>
      );
    },
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CURRENCY,
    headerName: 'Currency',
    flex: 1,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    flex: 1,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.UPDATED_AT,
    valueGetter: ({ row }) => row.updatedAt,
    headerName: 'Updated at',
    flex: 1,
    renderCell: ({ row }) => formatDate(row.updatedAt),
    sortComparator: (firstDate, secondDate) => compareDatesDescending(firstDate, secondDate),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CREATION_TIME,
    valueGetter: ({ row }) => row.externalPayment?.chargeTime,
    headerName: 'Transaction date',
    flex: 1,
    renderCell: ({ value }) => (
      <span
        className="font-sans text-black"
      >
        {isNotNullOrUndefined(value) ? formatDate(dayjs(value)) : getEmptyCellContent()}
      </span>
    ),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.PAYMENT_PROVIDER,
    headerName: 'Provider',
    flex: 1,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.DOT_MENU,
    valueGetter: ({ row }) => row.invoiceId,
    headerName: '',
    renderCell: ({ row }) => (
      <PaymentOverviewTableDotMenu
        invoiceId={row.invoiceId}
        externalInvoiceUrl={row.externalPayment?.receiptUrl}
      />
    ),
  },
];